<script setup lang="ts">
/**
 * The chat scope control (§6b), rendered as the bottom section of the composer card via `ChatInput`'s
 * `attached` slot.
 *
 * Why a full-width row and not a pill in the toolbar: the label is `Client › Case`, and real client
 * names ("JOSE NELSON RENDEROS MORALES") don't fit beside the model and QRA selectors — the pill
 * truncated the case away, leaving the chip showing the one part of the scope the user was least
 * likely to need reminding of. Given its own row it reads in full, and the picker below becomes the
 * same card unfolding rather than a second card floating underneath.
 *
 * Hidden entirely when the org has neither clients nor cases and the chat is unscoped — a scope
 * control over an empty ladder is a dead end. It used to test the CASES alone, which hid the control
 * outright from an org that had created clients but no case yet (AlexAmin/marcus#85): the first rung
 * of the ladder existed and was unreachable.
 */
import { Building2, Users, Globe, ChevronDown, X } from 'lucide-vue-next'
import { scopeLabel } from '~~/shared/web-chat/scope'
import { useScopePanel } from '~/composables/web-chat/useScopePanel'
import ChatScopePanel from '~/components/chat/ChatScopePanel.vue'
import ChatScopeOffer from '~/components/chat/ChatScopeOffer.vue'
import { Dialog, DialogContent, DialogTitle } from '~/components/ui/dialog'

const props = defineProps<{
  /** Docked sidebar / Word add-in: the panel body falls back to single-column drill-in. */
  compact?: boolean
  /**
   * Which half to draw (MAR-1057). The two-line web composer puts the `trigger`, a small pill, on the
   * line under the input beside the disclaimer, and the `panel` (the route's offer and the picker that
   * unfolds) above the input card. Both share `useScopePanel`'s open state. Unset: the whole bar, as
   * a section of its own.
   */
  part?: 'trigger' | 'panel'
}>()

const { t } = useI18n()
const webChat = useWebChatStore()
const { open, toggle, close, clients, cases, loaded, ensureScopeLists } = useScopePanel()

/**
 * Which expansion to MOUNT — not just which to show. Both variants rendered at once (CSS-hidden) would
 * each run their open-time setup, and the hidden one's autofocus would steal the caret from the visible
 * one. `md` matches the Tailwind breakpoint used below.
 */
const isDesktop = ref(true)
let mql: MediaQueryList | null = null
const syncViewport = (e: MediaQueryList | MediaQueryListEvent) => { isDesktop.value = e.matches }

const scope = computed(() => webChat.activeScope)
const scoped = computed(() => scope.value.kind !== 'global')
const label = computed(() => scopeLabel(scope.value) || t('clients.hub.scopeToCase'))
// Render straight away and hide only once we KNOW the org has neither — hiding by default would
// pop the bar in after the fetch and shift the composer on every load.
const visible = computed(() =>
  scoped.value || !loaded.value || clients.value.length > 0 || cases.value.length > 0)

function clear() {
  void webChat.setScope({ kind: 'global' })
}

onMounted(() => {
  mql = window.matchMedia('(min-width: 768px)')
  syncViewport(mql)
  mql.addEventListener('change', syncViewport)
  // The bar still needs the lists for its own `visible` check, but it no longer OWNS restoring
  // the remembered scope — that moved to `useRestoreScope()` in the app shell, because gating it on
  // this component's mount meant the scope (and the context rail with it) silently stayed off on
  // every browsing page until the user opened the chat.
  void ensureScopeLists()
})
onBeforeUnmount(() => mql?.removeEventListener('change', syncViewport))
</script>

<template>
  <!-- The route offering the rung it shows (a case or client hub), above the control it would
       change. Outside `visible`, which gates only the picker; the banner resolves its own names and
       renders itself away when there is nothing to offer, so no empty box is left behind. -->
  <ChatScopeOffer v-if="props.part !== 'trigger'" :compact="compact" />

  <!-- The pill: the line under the two-line composer (MAR-1057). -->
  <div
    v-if="props.part === 'trigger' && visible"
    class="inline-flex h-7 max-w-[16rem] min-w-0 items-center gap-0.5 rounded-full border border-border text-xs text-muted-foreground transition-colors"
    :class="scoped ? 'bg-muted text-foreground' : 'hover:bg-muted'"
  >
    <button
      type="button"
      class="flex min-w-0 items-center gap-1.5 rounded-full py-1 pl-2.5 text-left"
      :class="scoped ? 'pr-1' : 'pr-2'"
      :aria-expanded="open"
      @click="toggle"
    >
      <Building2 v-if="scope.kind === 'case'" class="h-3.5 w-3.5 shrink-0" />
      <Users v-else-if="scope.kind === 'client'" class="h-3.5 w-3.5 shrink-0" />
      <Globe v-else class="h-3.5 w-3.5 shrink-0" />
      <span class="min-w-0 truncate">{{ label }}</span>
      <ChevronDown v-if="!scoped" class="h-3 w-3 shrink-0 transition-transform" :class="open && 'rotate-180'" />
    </button>
    <button
      v-if="scoped"
      type="button"
      class="mr-1 shrink-0 rounded-full p-0.5 hover:bg-background hover:text-foreground"
      :title="t('clients.hub.untagCase')"
      :aria-label="t('clients.hub.untagCase')"
      @click="clear"
    >
      <X class="h-3 w-3" />
    </button>
  </div>

  <!-- Full: a section of the composer card (the card supplies the border + rounding).
       Compact: ChatInput has no card there, so carry our own chrome. -->
  <div
    v-if="visible && props.part !== 'trigger'"
    :class="props.part === 'panel'
      ? (open && isDesktop ? 'mb-2 overflow-hidden rounded-xl border border-border bg-card' : '')
      : compact
        ? 'overflow-hidden rounded-lg border border-border bg-card'
        : ''"
  >
    <!-- Resting row.
         Full mode carries NO separator and no fill: it is a footnote to the composer, in the register
         of the quiet "Project ▾ / Manual ▾" strip under Claude's composer, not a second row of the
         card. It used to draw `border-t border-border` plus `bg-muted` when scoped, which made the
         single most-often-untouched control the heaviest element on the start screen (MAR-111). -->
    <div
      v-if="props.part !== 'panel'"
      class="flex items-center gap-1.5 py-1.5 text-xs transition-colors"
      :class="compact
        ? (scoped ? 'gap-2 px-4 py-2 bg-muted' : 'gap-2 px-4 py-2 hover:bg-muted')
        : 'px-3'"
    >
      <button
        type="button"
        class="flex min-w-0 flex-1 items-center gap-1.5 rounded-md px-1 py-0.5 text-left transition-colors"
        :class="compact ? 'gap-2' : 'hover:bg-muted'"
        :aria-expanded="open"
        @click="toggle"
      >
        <Building2 v-if="scope.kind === 'case'" class="h-3.5 w-3.5 shrink-0" :class="compact ? 'text-accent-ink' : 'text-muted-foreground'" />
        <Users v-else-if="scope.kind === 'client'" class="h-3.5 w-3.5 shrink-0" :class="compact ? 'text-accent-ink' : 'text-muted-foreground'" />
        <Globe v-else class="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span
          class="min-w-0 flex-1 truncate text-muted-foreground"
          :class="scoped && compact && 'font-medium text-foreground'"
        >
          {{ label }}
        </span>
        <ChevronDown
          class="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform"
          :class="open && 'rotate-180'"
        />
      </button>
      <!-- Only while COLLAPSED. With the panel open, clearing lives in its footer alongside "back to
           chat" — keeping this here too put two competing X buttons within a few pixels. -->
      <button
        v-if="scoped && !open"
        type="button"
        class="shrink-0 rounded-md p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        :title="t('clients.hub.untagCase')"
        @click="clear"
      >
        <X class="h-3.5 w-3.5" />
      </button>
    </div>

    <!-- Expansion, DESKTOP: a fixed 50vh section of the composer card. Fixed rather than
         content-driven — sizing it to the columns made the composer jump every time you moved between
         a client and its cases, which is what made the picker feel unstable. Height is animated via
         `grid-rows-[0fr] → [1fr]` so it still unfolds rather than snapping. -->
    <Transition
      enter-active-class="transition-[grid-template-rows,opacity] duration-200 ease-out"
      enter-from-class="grid-rows-[0fr] opacity-0"
      leave-active-class="transition-[grid-template-rows,opacity] duration-150 ease-in"
      leave-to-class="grid-rows-[0fr] opacity-0"
    >
      <div v-if="open && isDesktop" class="grid grid-rows-[1fr]">
        <div class="min-h-0 overflow-hidden">
          <div class="h-[50vh]">
            <ChatScopePanel :compact="compact" />
          </div>
        </div>
      </div>
    </Transition>
  </div>

  <!-- Expansion, MOBILE: a full-screen `ui/dialog` sheet (MAR-948). Portalled to <body> because the
       composer card clips its children (`.chat-box` has overflow:hidden), and a phone has no room for
       a half-height picker above the keyboard anyway. No corner ✕: the panel's footer already has
       "back to chat", and Escape closes it through the same `close()`. -->
  <Dialog v-if="props.part !== 'trigger'" :open="open && visible && !isDesktop" @update:open="(o) => { if (!o) close() }">
    <DialogContent
      class="inset-0 left-0 top-0 flex h-full max-w-none translate-x-0 translate-y-0 flex-col gap-0 rounded-none border-0 bg-background p-0 sm:rounded-none data-[state=open]:animate-none data-[state=closed]:animate-none"
      style="padding-top: env(safe-area-inset-top); padding-bottom: env(safe-area-inset-bottom)"
      :show-close-button="false"
      :aria-describedby="undefined"
    >
      <DialogTitle class="sr-only">{{ t('clients.hub.scopePanelTitle') }}</DialogTitle>
      <ChatScopePanel compact />
    </DialogContent>
  </Dialog>
</template>
